import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeaderButtons } from '../header-buttons.component';
import useAuth from '../../../../auth/feature/auth';
import { authService } from '../../../../auth/service/auth.service';
import { queryClient } from '../../../../app/queryClient';
import { BREAKPOINTS, ROUTER_LINKS } from '../../../consts/app-keys.const';
import { LanguageSelector } from '../../language-selector.component';
import { StandbyMark } from '../../icons/standby-mark.component';
import { useTheme } from '../../../hooks/use-theme';
import { useRiserPage } from '../../../../riser/feature/use-riser-page';
import { useHasFrameworkAccess } from '../../../../sbframework/feature/use-sbframework-access';
import subdivsLogo from '../../../../../assets/images/squarebit-subdivs/subdivs-logo.svg';
import subdivsLogoDark from '../../../../../assets/images/squarebit-subdivs/subdivs-logo-dark.svg';

/** One row in the products accordion: icon column then label. */
const PRODUCT_LINK_CLASS =
  'font-body text-[14px] text-content-muted-alt py-[8px] px-[4px] flex items-center gap-[10px]';
/** Fixed width so labels align whether or not a row has artwork. */
const PRODUCT_ICON_CLASS =
  'shrink-0 w-[18px] h-[18px] flex items-center justify-center overflow-hidden';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  /** Set by the header to hold one surface across the bar and this panel. */
  forcedTheme?: 'dark';
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  forcedTheme
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { isAuthenticated, setIsAuthenticated, user, isAdmin } = useAuth();
  const emailPrefix = user?.email ? user.email.split('@')[0] : '';
  const initials = emailPrefix.slice(0, 2).toUpperCase();
  const displayEmail = user?.email ?? '';
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutConfirming, setLogoutConfirming] = useState(false);

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    queryClient.clear();
    onClose();
    navigate(ROUTER_LINKS.ROOT);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (!isOpen) setLogoutConfirming(false);
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on navigation, for every width that can see this drawer. It used to
  // test `tablet`, which stopped matching the drawer's own range once the
  // header started collapsing at `headerNav` — a tablet between the two would
  // navigate and leave the menu sitting open over the new page.
  useEffect(() => {
    if (window.innerWidth < BREAKPOINTS.headerNav) {
      isOpen && onClose();
    }
  }, [location.pathname]);

  // Granted products, gated exactly as the desktop dropdown gates them: the
  // framework on the account's grant, Riser on its gated page endpoint, whose
  // answer also supplies the entry's name so none of it is written here.
  const { granted: hasFramework } = useHasFrameworkAccess();
  const riser = useRiserPage();

  const [productsOpen, setProductsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    const menu = menuRef.current;
    if (!menu) return;

    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(menu.querySelectorAll<HTMLElement>(FOCUSABLE));

    getFocusable()[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  return (
    <div
      id="mobile-menu"
      data-theme={forcedTheme}
      ref={menuRef}
      className={`fixed top-[76px] left-0 w-full h-[calc(100vh-76px)] bg-surface-base shadow-lg py-[28px] px-[24px] flex flex-col z-50 transition-all overflow-y-auto ${
        isOpen
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-5 pointer-events-none'
      }`}
    >
      <nav className="flex flex-col gap-[4px] mb-[24px]">
        {/* Mirrors the desktop bar: Services leads, as its own destination
            rather than a row buried in the Products accordion. */}
        <NavLink
          to={ROUTER_LINKS.RIGGING_SERVICES}
          onClick={onClose}
          className="font-body text-[16px] text-content-dark py-[10px] px-[4px] border-b border-border-dark/[0.06]"
        >
          {t('header.services')}
        </NavLink>
        <div>
          <button
            type="button"
            onClick={() => setProductsOpen(!productsOpen)}
            className="w-full flex items-center justify-between font-body text-[16px] text-content-dark py-[10px] px-[4px] border-b border-border-dark/[0.06]"
          >
            {t('header.products')}
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className={`transition-transform duration-150 ${productsOpen ? 'rotate-180' : 'rotate-0'}`}
            >
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {productsOpen && (
            <div className="flex flex-col gap-[2px] pl-[16px] py-[6px]">
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-content-muted-alt px-[4px] pt-[2px] pb-[4px]">
                {t('products.groupProducts')}
              </div>
              {/* Icons match the desktop dropdown, including its theme-aware
                  artwork — the Subdivs badge and the TouchPose glyph each ship
                  a light and a dark cut, and the wrong one disappears into the
                  surface. Standby's mark takes currentColor instead, so it
                  needs no pair. */}
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_SUBDIVS}
                onClick={onClose}
                className={PRODUCT_LINK_CLASS}
              >
                <span className={PRODUCT_ICON_CLASS}>
                  <img
                    src={isDark ? subdivsLogoDark : subdivsLogo}
                    alt=""
                    className="w-full h-full scale-125 rounded-[4px]"
                  />
                </span>
                Squarebit Subdivs
              </NavLink>
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_EYE}
                onClick={onClose}
                className={PRODUCT_LINK_CLASS}
              >
                <span className={PRODUCT_ICON_CLASS}>
                  <span className="text-[13px] leading-none grayscale">👁️</span>
                </span>
                Squarebit Eye
              </NavLink>
              <NavLink
                to={ROUTER_LINKS.TOUCHPOSE}
                onClick={onClose}
                className={PRODUCT_LINK_CLASS}
              >
                <span className={PRODUCT_ICON_CLASS}>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/${
                      isDark ? 'touchpose_icon_dark.svg' : 'touchpose_icon.svg'
                    }`}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </span>
                TouchPose
              </NavLink>
              {/* This product list is maintained separately from the desktop
                  dropdown's, which is why every product has to be stated
                  twice. Worth collapsing into one source some day. */}
              <NavLink
                to={ROUTER_LINKS.STANDBY}
                onClick={onClose}
                className={PRODUCT_LINK_CLASS}
              >
                <span className={`${PRODUCT_ICON_CLASS} text-content-primary`}>
                  <StandbyMark size={15} />
                </span>
                Standby
              </NavLink>
              {hasFramework && (
                <NavLink
                  to={ROUTER_LINKS.SBFRAMEWORK}
                  onClick={onClose}
                  className={PRODUCT_LINK_CLASS}
                >
                  <span className={PRODUCT_ICON_CLASS}>
                    <span className="text-[13px] leading-none grayscale">⚙️</span>
                  </span>
                  Framework
                </NavLink>
              )}
              {riser.page && (
                <NavLink
                  to={ROUTER_LINKS.RISER}
                  onClick={onClose}
                  className={PRODUCT_LINK_CLASS}
                >
                  <span className={PRODUCT_ICON_CLASS}>
                    <span className="text-[13px] leading-none grayscale">📍</span>
                  </span>
                  {riser.page.nav.name}
                </NavLink>
              )}
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setResourcesOpen(!resourcesOpen)}
            className="w-full flex items-center justify-between font-body text-[16px] text-content-dark py-[10px] px-[4px] border-b border-border-dark/[0.06]"
          >
            {t('header.resources')}
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className={`transition-transform duration-150 ${resourcesOpen ? 'rotate-180' : 'rotate-0'}`}
            >
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {resourcesOpen && (
            <div className="flex flex-col gap-[2px] pl-[16px] py-[6px]">
              <div className="font-body text-[10px] font-bold tracking-[0.08em] text-blue uppercase pt-[4px] pb-[2px] px-[4px]">
                TouchPose
              </div>
              {[
                {
                  key: 'docs',
                  label: t('resourcesDropdown.docs'),
                  to: ROUTER_LINKS.TOUCHPOSE_DOCS
                },
                {
                  key: 'changelog',
                  label: t('resourcesDropdown.changelog'),
                  to: ROUTER_LINKS.TOUCHPOSE_CHANGELOG
                },
                {
                  key: 'roadmap',
                  label: t('resourcesDropdown.roadmap'),
                  to: ROUTER_LINKS.TOUCHPOSE_ROADMAP
                },
                {
                  key: 'faq',
                  label: t('resourcesDropdown.faq'),
                  to: ROUTER_LINKS.TOUCHPOSE_FAQ
                }
              ].map(({ key, label, to }) => (
                <NavLink
                  key={key}
                  to={to}
                  onClick={onClose}
                  className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
                >
                  {label}
                </NavLink>
              ))}
              <div className="font-body text-[10px] font-bold tracking-[0.08em] text-blue uppercase pt-[10px] pb-[2px] px-[4px]">
                Squarebit Subdivs
              </div>
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_SUBDIVS_DOCS}
                onClick={onClose}
                className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
              >
                {t('resourcesDropdown.docs')}
              </NavLink>
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_SUBDIVS_CHANGELOG}
                onClick={onClose}
                className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
              >
                {t('resourcesDropdown.changelog')}
              </NavLink>
              <div className="font-body text-[10px] font-bold tracking-[0.08em] text-blue uppercase pt-[10px] pb-[2px] px-[4px]">
                Squarebit Eye
              </div>
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_EYE_DOCS}
                onClick={onClose}
                className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
              >
                {t('resourcesDropdown.docs')}
              </NavLink>
              <NavLink
                to={ROUTER_LINKS.SQUAREBIT_EYE_CHANGELOG}
                onClick={onClose}
                className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
              >
                {t('resourcesDropdown.changelog')}
              </NavLink>
              <a
                href="https://discord.gg/u3rpHEg6Ct"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px] mt-[6px]"
              >
                {t('resourcesDropdown.discord')}
              </a>
            </div>
          )}
        </div>

        <NavLink
          to={ROUTER_LINKS.MODELS}
          onClick={onClose}
          className="font-body text-[16px] text-content-dark py-[10px] px-[4px] border-b border-border-dark/[0.06]"
        >
          {t('header.library')}
        </NavLink>
        <NavLink
          to={ROUTER_LINKS.CONTACT}
          onClick={onClose}
          className="font-body text-[16px] text-content-dark py-[10px] px-[4px] border-b border-border-dark/[0.06]"
        >
          {t('header.contact')}
        </NavLink>

        <div className="py-[10px] px-[4px] border-b border-border-dark/[0.06]">
          <LanguageSelector direction="down" />
        </div>
      </nav>

      {!isAuthenticated ? (
        <div className="flex mb-[183px] mt-[42px] gap-[22px]">
          <HeaderButtons />
        </div>
      ) : (
        <div className="flex flex-col border-t border-blue-100 mb-[85px] mt-[22px] pt-[23px] gap-[12px] text-[16px] font-body text-content-dark">
          <div className="flex items-center gap-[10px] mb-[4px]">
            <span className="flex items-center justify-center w-[36px] h-[36px] rounded-full bg-blue-500 text-blue-deep-2 text-[13px] font-semibold shrink-0">
              {initials}
            </span>
            <span className="text-[13px] text-content-muted-alt truncate">
              {displayEmail}
            </span>
          </div>
          <Link
            to={ROUTER_LINKS.MANAGEMENT}
            className="text-left font-body text-medium text-[16px]"
            onClick={onClose}
          >
            {t('header.myProducts')}
          </Link>
          <Link
            to={ROUTER_LINKS.SETTINGS}
            className="text-left font-body text-medium text-[16px]"
            onClick={onClose}
          >
            {t('header.settings')}
          </Link>
          {isAdmin && (
            <div>
              <button
                type="button"
                onClick={() => setAdminOpen(!adminOpen)}
                className="w-full flex items-center justify-between font-body text-[16px] text-left"
              >
                {t('header.admin')}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={`transition-transform duration-150 shrink-0 ${adminOpen ? 'rotate-180' : 'rotate-0'}`}
                >
                  <path
                    d="M2 4l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {adminOpen && (
                <div className="flex flex-col gap-[2px] pl-[16px] py-[6px]">
                  <Link
                    to={ROUTER_LINKS.ADMIN}
                    onClick={onClose}
                    className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
                  >
                    {t('header.dashboard')}
                  </Link>
                  <Link
                    to={ROUTER_LINKS.ADMIN_METRICS}
                    onClick={onClose}
                    className="font-body text-[14px] text-content-muted-alt py-[8px] px-[4px]"
                  >
                    {t('header.metrics')}
                  </Link>
                </div>
              )}
            </div>
          )}
          {logoutConfirming ? (
            <div className="flex items-center gap-[10px] flex-wrap">
              <span className="font-body text-[14px] text-content-secondary mr-[2px]">
                {t('header.logoutConfirm')}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="font-body text-[13px] font-semibold text-red-500 border border-red-400 rounded-full px-[14px] py-[5px] bg-transparent cursor-pointer hover:bg-red-50 transition-colors duration-150"
              >
                {t('header.logoutConfirmYes')}
              </button>
              <button
                type="button"
                onClick={() => setLogoutConfirming(false)}
                className="font-body text-[13px] text-content-secondary border border-border rounded-full px-[14px] py-[5px] bg-transparent cursor-pointer hover:bg-surface-hover transition-colors duration-150"
              >
                {t('header.logoutConfirmNo')}
              </button>
            </div>
          ) : (
            <button
              className="text-left font-body text-medium text-[16px]"
              onClick={() => setLogoutConfirming(true)}
            >
              {t('header.logout')}
            </button>
          )}
        </div>
      )}

      {/* <div className="flex gap-[28px]">
        <button className="flex items-center gap-[6px]">
          <p className="font-body text-medium text-[16px]">Terms</p>
          <ArrowTopRightIcon />
        </button>
        <button className="flex items-center gap-[6px]">
          <p className="font-body text-medium text-[16px]">Privacy</p>
          <ArrowTopRightIcon />
        </button>
      </div>

      <div className="mt-[24px]">
        <a
          href="mailto:help@gmail.com"
          className="font-body text-medium text-[16px]"
        >
          help@gmail.com
        </a>
      </div> */}
    </div>
  );
};
